import { areIntervalsOverlapping } from 'date-fns';
import { TaskIcon } from 'app/agriculture/icons';
import { groupByKey } from 'app/agriculture/utils';
import { useTimelineInterval } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { TaskCategory, TaskCategoryEnum } from 'app/agriculture/models/entities/task/TaskCategory';
import { isTaskOverdue } from 'app/agriculture/models/entities/task/TaskUtils';
import { TimelineRow } from '../TimelineRow';
import { useTasks } from 'app/agriculture/queries/tasks/useTasks';
import { TimelineEmptyState } from 'app/agriculture/AgTimeline/components/TimelineEmptyState/TimelineEmptyState';
import { TimelinePellets } from 'app/agriculture/AgTimeline/components/TimelinePellet/TimelinePellets';
import { TimelineTaskPopoverContent } from 'app/agriculture/AgTimeline/components/TimelineTaskPopoverContent/TimelineTaskPopoverContent';
import { useMemo } from 'react';

export function TimelineTaskRows() {
    const interval = useTimelineInterval();
    const tasks = useTasks();

    const filteredTasks = useMemo(
        () =>
            [...tasks.values()].filter(({ startDate, endDate }) =>
                areIntervalsOverlapping({ start: startDate, end: endDate }, interval),
            ),
        [tasks, interval],
    );

    if (tasks.size === 0) {
        return <TimelineEmptyState message="Nothing to display. Add tasks to start tracking." />;
    }

    if (filteredTasks.length === 0) {
        return <TimelineEmptyState message="There are no tasks in this time period." />;
    }

    const groupedAndFilteredTasks = groupByKey(filteredTasks, 'category');

    return (
        <>
            {Object.entries(groupedAndFilteredTasks)
                .sort(
                    ([a], [b]) =>
                        TaskCategoryEnum.options.indexOf(a as TaskCategory) -
                        TaskCategoryEnum.options.indexOf(b as TaskCategory),
                )
                .map(([category, tasksForCategory]) => {
                    if (tasksForCategory.length === 0) return null;

                    return (
                        <TimelineRow key={category} heading={category}>
                            <TimelinePellets
                                items={tasksForCategory.map((task) => {
                                    const color = task.priority == 'Normal' ? 'primary' : 'warning';
                                    return {
                                        id: String(task.id),
                                        text: task.title,
                                        Icon: TaskIcon,
                                        interval: {
                                            start: task.startDate,
                                            end: task.endDate,
                                        },
                                        color: isTaskOverdue(task) ? 'danger' : color,
                                        disabled: task.isComplete,
                                        popoverContent: (
                                            <TimelineTaskPopoverContent taskId={task.id} />
                                        ),
                                    };
                                })}
                            />
                        </TimelineRow>
                    );
                })}
        </>
    );
}
